import { create, type StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { UseBoundStore } from 'zustand/react';
import type { Order, Position, PositionStatusReason } from '@/api/spec';
import { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import { mapApiPositionStatus, terminalPositionStatuses, type ViewPosition } from '@/feature/terminal_tables/position/types';
import { BigIntDevtoolsSerialize } from '@/utils/zustand_helpers';
import { buildMapByKey, patchMapWithUpsertAndRemoval } from '@/utils/zustandListStore';
import type { ListStore } from '../../../common/ListStore';
import { mapPositionsToViewModel } from './mapPositionsToViewModel';
import { getPositionErrorInfo } from './positionNotificationMessages';
import { handlePositionStatusUpdate } from './positionNotifications';

export type PositionPostingStatus = 'idle' | 'preparing' | 'confirming' | 'confirmed' | 'failed';

export interface PositionPostingState {
  marketId?: number;
  requestId?: number;
  status: PositionPostingStatus;
  error?: string;
}

const createPositionKey = (position: ViewPosition): string => {
  return `${position.positionId}-${position.marketId}`;
};

const createRawPositionKey = (position: Position): string => {
  return `${position.pid}-${position.mkt}`;
};

type PositionQuickFilter = 'all' | typeof Side.long | typeof Side.short;

function isPositionQuickFilter(value: unknown): value is PositionQuickFilter {
  return value === 'all' || value === Side.long || value === Side.short;
}

export interface PositionFilters {
  sideSelected?: 'all' | typeof Side.long | typeof Side.short;
}

type RecalculationReason = 'interval' | 'market-update' | 'manual' | string;

interface PositionStore extends ListStore<ViewPosition> {
  // Data
  positions: ViewPosition[];
  positionsMap: Map<string, ViewPosition>;
  rawPositions: Map<string, Position>;

  // Filters
  filters: PositionFilters;

  // Computed
  filteredData: ViewPosition[];
  lastRecalculatedAt?: number;
  lastRecalculationReason?: RecalculationReason;

  // Posting state
  posting: PositionPostingState;
  setPostingPreparing: (marketId: number, requestId: number) => void;
  setPostingConfirming: () => void;
  setPostingFailed: (reason: PositionStatusReason, context?: string) => void;
  resetPosting: () => void;

  // Filter actions
  setSideFilter: (side: PositionFilters['sideSelected']) => void;
  setQuickFilter: (filter?: string) => void;

  // Data actions
  setRawPositionsSnapshot: (rawPositions: Position[]) => void;
  patchRawPositions: (rawPositions: Position[]) => void;
  recalculateFromCurrentPrices: (options: {
    markets: ReadonlyMap<number, MarketData>;
    orders: Order[];
    marketIds?: number[];
    reason?: RecalculationReason;
  }) => void;
}

const initialState: Pick<
  PositionStore,
  | 'positions'
  | 'positionsMap'
  | 'filters'
  | 'filteredData'
  | 'rawPositions'
  | 'lastRecalculatedAt'
  | 'lastRecalculationReason'
  | 'lastRecalculationReason'
  | 'posting'
> = {
  positions: [],
  positionsMap: new Map(),
  rawPositions: new Map(),
  filters: {
    sideSelected: 'all',
  },
  filteredData: [],
  lastRecalculatedAt: undefined,
  lastRecalculationReason: undefined,
  posting: {
    status: 'idle',
  },
};

const applyFilters = (state: Pick<PositionStore, 'positions' | 'filters'>): ViewPosition[] => {
  const side = state.filters.sideSelected ?? 'all';
  return state.positions.filter((p) => side === 'all' || p.side === side);
};

export const usePositionStore: UseBoundStore<StoreApi<PositionStore>> = create<PositionStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      // Filter actions
      setSideFilter: (side) =>
        set((state) => {
          state.filters.sideSelected = side ?? 'all';
          state.filteredData = applyFilters(state);
        }),
      setQuickFilter: (filter) =>
        set((state) => {
          state.filters.sideSelected = isPositionQuickFilter(filter) ? filter : 'all';
          state.filteredData = applyFilters(state);
        }),

      // Posting actions
      setPostingPreparing: (marketId, requestId) =>
        set((state) => {
          state.posting.status = 'preparing';
          state.posting.marketId = marketId;
          state.posting.requestId = requestId;
          state.posting.error = undefined;
        }),
      setPostingConfirming: () =>
        set((state) => {
          if (state.posting.status === 'preparing') {
            state.posting.status = 'confirming';
          }
        }),
      setPostingFailed: (reason, context) =>
        set((state) => {
          state.posting.status = 'failed';
          state.posting.error = getPositionErrorInfo(reason, context).message;
        }),
      resetPosting: () =>
        set((state) => {
          state.posting = initialState.posting;
        }),

      // Raw data actions
      setRawPositionsSnapshot: (rawItems) =>
        set((state) => {
          state.rawPositions = buildMapByKey(rawItems, createRawPositionKey);
        }),
      patchRawPositions: (rawUpdates) =>
        set((state) => {
          if (rawUpdates.length === 0) return;
          patchMapWithUpsertAndRemoval(state.rawPositions, rawUpdates, createRawPositionKey, (position) =>
            terminalPositionStatuses.has(mapApiPositionStatus(position.st))
          );

          rawUpdates.forEach((position) => {
            // If we receive an update for the market we are posting to, mark it as confirmed
            if (
              state.posting.marketId === position.mkt &&
              (state.posting.status === 'preparing' || state.posting.status === 'confirming')
            ) {
              state.posting.status = 'confirmed';
            }
          });
        }),

      // View data actions
      setAll: (items) =>
        set((state) => {
          state.positions = items;
          state.positionsMap = buildMapByKey(items, createPositionKey);
          state.filteredData = applyFilters(state);
          state.lastRecalculatedAt = Date.now();
          state.lastRecalculationReason = 'positions-snapshot';
        }),
      patch: (updates) =>
        set((state) => {
          patchMapWithUpsertAndRemoval(
            state.positionsMap,
            updates,
            createPositionKey,
            (p) => terminalPositionStatuses.has(p.status),
            (updated, existing) => {
              if (!existing) return;
              handlePositionStatusUpdate(updated, existing.status);
            }
          );
          state.positions = Array.from(state.positionsMap.values());
          state.filteredData = applyFilters(state);
          state.lastRecalculatedAt = Date.now();
          state.lastRecalculationReason = 'positions-patch';
        }),
      recalculateFromCurrentPrices: (options) =>
        set((state) => {
          state.lastRecalculatedAt = Date.now();
          state.lastRecalculationReason = options.reason;

          if (state.rawPositions.size === 0) return;
          const markets = options.markets;
          if (!markets || markets.size === 0) return;

          const marketIds = options.marketIds;
          const rawPositions = marketIds?.length
            ? Array.from(state.rawPositions.values()).filter((position) => marketIds.includes(position.mkt))
            : Array.from(state.rawPositions.values());

          if (rawPositions.length === 0) return;

          const updatedViewPositions = mapPositionsToViewModel(rawPositions, markets, options.orders);
          if (updatedViewPositions.length === 0) return;

          if (marketIds?.length) {
            updatedViewPositions.forEach((position) => {
              state.positionsMap.set(createPositionKey(position), position);
            });
            state.positions = Array.from(state.positionsMap.values());
          } else {
            state.positions = updatedViewPositions;
            state.positionsMap = buildMapByKey(updatedViewPositions, createPositionKey);
          }
          state.filteredData = applyFilters(state);
        }),
      reset: () =>
        set(() => ({
          ...initialState,
          positionsMap: new Map(), // Ensure map is new
          rawPositions: new Map(),
          posting: initialState.posting,
        })),
    })),
    {
      name: 'dex-positions',
      ...BigIntDevtoolsSerialize,
    }
  )
);
